import { useState } from "react";
import { motion } from "framer-motion";
import { useGameStore } from "../../store/gameStore";
import { useLanguage } from "../../i18n/LanguageContext";
import Button from "../ui/Button";
import AdUnit from "../ui/AdUnit";

const AVATARS = ["🦁", "🐼", "🦊", "🐸", "🦋", "🐯"];


export default function SetupScreen() {
  const initGame = useGameStore((s) => s.initGame);
  const { t, lang, setLang } = useLanguage();
  const [names, setNames] = useState(["", ""]);
  const [totalRounds, setTotalRounds] = useState(8);

  const validNames = names.filter((n) => n.trim());

  const addPlayer = () => { if (names.length < 6) setNames([...names, ""]); };
  const removePlayer = (idx: number) => { if (names.length > 2) setNames(names.filter((_, i) => i !== idx)); };
  const updateName = (idx: number, value: string) => {
    const next = [...names]; next[idx] = value; setNames(next);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 font-display flex items-center justify-center p-4 pb-10 relative overflow-y-auto">


      {/* Radial glow */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(99,102,241,0.12),transparent_65%)]" />

      <motion.div
        className="w-full max-w-md space-y-5 relative z-10"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Logo */}
        <div className="text-center relative">
          <button
            onClick={() => setLang(lang === "en" ? "hy" : "en")}
            className="absolute right-0 top-0 text-sm text-white/40 hover:text-white/80 transition-colors bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-xl border border-white/10"
          >
            {lang === "en" ? "🇦🇲 ՀՅ" : "🇬🇧 EN"}
          </button>

          <motion.div
            className="text-7xl mb-3 inline-block"
            animate={{ rotate: [0, -6, 6, 0], scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
          >
            💎
          </motion.div>
          <h1 className="text-5xl font-black text-white tracking-tight">VereV</h1>
          <p className="text-white/40 mt-2 text-sm leading-relaxed max-w-xs mx-auto">{t.setup.tagline}</p>
        </div>

        {/* Players */}
        <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl border border-white/10 p-5 space-y-4">
          <div className="font-bold text-white text-sm">{t.setup.players}</div>
          <div className="space-y-2">
            {names.map((name, idx) => (
              <motion.div
                key={idx}
                className="flex items-center gap-2"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.07 }}
              >
                <span className="text-2xl">{AVATARS[idx]}</span>
                <input
                  type="text"
                  placeholder={t.setup.playerPlaceholder(idx + 1)}
                  value={name}
                  onChange={(e) => updateName(idx, e.target.value)}
                  maxLength={16}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white placeholder-white/20 focus:outline-none focus:border-earnings/60 focus:bg-white/8 text-sm font-display transition-colors"
                />
                {names.length > 2 && (
                  <button onClick={() => removePlayer(idx)} className="text-white/20 hover:text-white/60 transition-colors text-xl leading-none">×</button>
                )}
              </motion.div>
            ))}
          </div>
          {names.length < 6 && (
            <button onClick={addPlayer} className="text-sm text-earnings/60 hover:text-earnings transition-colors font-semibold">
              {t.setup.addPlayer}
            </button>
          )}
        </div>

        {/* Round count */}
        <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl border border-white/10 p-5 space-y-3">
          <div className="font-bold text-white text-sm">{t.setup.gameLength}</div>
          <div className="grid grid-cols-3 gap-2">
            {([6, 8, 10] as const).map((rounds, i) => (
              <button
                key={rounds}
                onClick={() => setTotalRounds(rounds)}
                className={`p-3 rounded-xl border text-center transition-all ${
                  totalRounds === rounds
                    ? "border-earnings bg-earnings/20 text-white shadow-lg shadow-earnings/10"
                    : "border-white/10 text-white/50 hover:border-white/25 hover:text-white/70"
                }`}
              >
                <div className="font-bold text-xs leading-tight">{t.setup.gameLengths[i].label}</div>
                <div className="text-xs opacity-70 mt-0.5 whitespace-nowrap">{rounds} {t.setup.rounds}</div>
                <div className="text-xs opacity-40 whitespace-nowrap">{t.setup.gameLengths[i].time}</div>
              </button>
            ))}
          </div>
        </div>

        <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
          <Button
            onClick={() => initGame(names, totalRounds)}
            disabled={validNames.length < 2}
            size="lg"
            className="w-full text-base font-black tracking-wide"
          >
            {t.setup.startGame}
          </Button>
        </motion.div>

        <div className="text-center text-white/20 text-xs leading-relaxed">
          {t.setup.footer1}<br />{t.setup.footer2}
        </div>

        <AdUnit />
      </motion.div>
    </div>
  );
}
